import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import logo from "./assets/icons/logo.jpg";
import { motion, AnimatePresence } from 'framer-motion';
import TurkishLeagueHeader from './TurkishLeagueHeader';

const links = [
    { to: '/', label: 'Anasayfa' },
    { to: '/about', label: 'Hakkımızda' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'İletişim' },
];

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scoresHidden, setScoresHidden] = useState(false);
    const lastY = useRef(0);
    const headerRef = useRef(null);
    const location = useLocation();
    const toggleMenu = () => setIsOpen(!isOpen);
    const isHome = location.pathname === '/';

    const isActive = (path) =>
        path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

    useEffect(() => {
        setIsOpen(false);
        setScoresHidden(false);
        lastY.current = window.scrollY;
    }, [location.pathname]);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;

        const applyHeight = () => {
            document.documentElement.style.setProperty(
                '--site-header-h',
                `${el.offsetHeight}px`
            );
        };

        applyHeight();
        const ro = new ResizeObserver(applyHeight);
        ro.observe(el);
        return () => {
            ro.disconnect();
            document.documentElement.style.removeProperty('--site-header-h');
        };
    }, [location.pathname, scoresHidden, isOpen]);

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;

            if (isOpen || !isHome) {
                setScoresHidden(false);
                lastY.current = y;
                return;
            }

            if (y < 48) {
                setScoresHidden(false);
            } else if (y > lastY.current + 6) {
                setScoresHidden(true);
            } else if (y < lastY.current - 6) {
                setScoresHidden(false);
            }
            lastY.current = y;
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [isOpen, isHome]);

    return (
        <>
            <header ref={headerRef} className="fixed top-0 inset-x-0 z-50">
                {isHome && (
                    <div
                        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                            scoresHidden ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
                        }`}
                    >
                        <div className="overflow-hidden">
                            <TurkishLeagueHeader />
                        </div>
                    </div>
                )}

                <div className="w-full bg-white border-b border-[#e8e6e1]">
                    <div className="w-11/12 max-w-7xl mx-auto flex items-center justify-between h-14 md:h-16">
                        <Link
                            to="/"
                            className="flex items-center gap-3 min-w-0"
                            onClick={() => setIsOpen(false)}
                        >
                            <img src={logo} alt="Sports Digitale" className="w-9 h-9 object-contain" />
                            <span className="font-display text-lg md:text-xl tracking-tight truncate text-[#111]">
                                Sports Digitale
                            </span>
                        </Link>

                        <nav className="hidden md:flex items-center gap-1">
                            {links.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className={`relative px-3 py-2 text-[13px] font-semibold tracking-wide transition-colors ${
                                        isActive(link.to)
                                            ? 'text-[#111]'
                                            : 'text-[#333] hover:text-[#111]'
                                    }`}
                                >
                                    {link.label}
                                    {isActive(link.to) && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute left-3 right-3 -bottom-0.5 h-px bg-[var(--accent)]"
                                        />
                                    )}
                                </Link>
                            ))}
                        </nav>

                        <button
                            type="button"
                            onClick={toggleMenu}
                            className="md:hidden p-2 text-[#111] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                            aria-label={isOpen ? 'Menüyü kapat' : 'Menüyü aç'}
                        >
                            {isOpen ? <FiX size={22} /> : <FiMenu size={22} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {isOpen && (
                    <motion.nav
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-40 bg-[var(--surface)] flex flex-col justify-center items-center gap-8 md:hidden"
                    >
                        <button
                            type="button"
                            onClick={toggleMenu}
                            className="absolute top-5 right-[calc(4.166%+0.5rem)] p-2 text-[var(--ink)]"
                            aria-label="Menüyü kapat"
                        >
                            <FiX size={28} />
                        </button>
                        {links.map((link, i) => (
                            <motion.div
                                key={link.to}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.05 * i }}
                            >
                                <Link
                                    to={link.to}
                                    onClick={toggleMenu}
                                    className={`font-display text-3xl ${
                                        isActive(link.to) ? 'text-[var(--accent)]' : 'text-[var(--ink)]'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            </motion.div>
                        ))}
                    </motion.nav>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
